import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortalSession } from '../services/api';
import type { UserTier } from '../types';

interface SidebarProps {
  currentPath: string;
  email: string | null;
  usageRemaining: number | null;
  tier?: UserTier;
  onSignOut: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const tools: { to: string; label: string; proOnly?: boolean }[] = [
  { to: '/editor', label: 'Executive Polish' },
  { to: '/coach', label: 'PM Coach' },
];

export function Sidebar({
  currentPath,
  email,
  usageRemaining,
  tier,
  onSignOut,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const [portalLoading, setPortalLoading] = useState(false);

  async function handlePortal() {
    if (portalLoading) return;
    setPortalLoading(true);
    try {
      const url = await createPortalSession();
      window.open(url, '_blank');
    } catch {
      // Silently ignore — Pro-only endpoint, free users hit this from the
      // sidebar too. Real surfacing happens via API error states elsewhere.
    } finally {
      setPortalLoading(false);
    }
  }

  function linkStyle(to: string): React.CSSProperties {
    const active = currentPath === to;
    return {
      display: 'block',
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      textDecoration: 'none',
      color: active ? 'var(--accent)' : 'var(--text-secondary)',
      background: active ? 'var(--accent-bg)' : 'transparent',
      transition: 'background 0.15s, color 0.15s',
    };
  }

  return (
    <>
      {mobileOpen && (
        <div
          onClick={onMobileClose}
          className="sidebar-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 60,
          }}
        />
      )}

      <aside
        className={`sidebar ${mobileOpen ? 'sidebar-open' : ''}`}
        style={{
          width: '220px',
          background: 'var(--nav-bg)',
          borderRight: '1px solid var(--nav-border)',
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          zIndex: 70,
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 16px',
        }}
      >
        <Link
          to="/dashboard"
          onClick={onMobileClose}
          style={{
            textDecoration: 'none',
            marginBottom: '24px',
            padding: '0 12px',
            display: 'block',
          }}
        >
          <span
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '20px',
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            <span style={{ color: 'var(--accent)' }}>My</span>
            <span style={{ color: 'var(--text-primary)' }}>IQ</span>
          </span>
        </Link>

        <SectionLabel>Tools</SectionLabel>
        {tools.map(item => {
          const showProBadge =
            item.proOnly && tier !== undefined && tier !== 'pro';
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onMobileClose}
              style={{
                ...linkStyle(item.to),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <span>{item.label}</span>
              {showProBadge && (
                <span
                  style={{
                    fontSize: '9px',
                    fontWeight: 600,
                    color: '#a78bfa',
                    border: '1px solid rgba(167,139,250,0.4)',
                    borderRadius: '20px',
                    padding: '1px 6px',
                    fontFamily: "'Outfit', sans-serif",
                    letterSpacing: '0.04em',
                  }}
                >
                  PRO
                </span>
              )}
            </Link>
          );
        })}

        <div style={{ height: '20px' }} />

        <SectionLabel>Account</SectionLabel>
        {tier === 'pro' ? (
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            style={{
              ...linkStyle(''),
              background: 'transparent',
              border: 'none',
              cursor: portalLoading ? 'wait' : 'pointer',
              textAlign: 'left',
              width: '100%',
              fontFamily: 'inherit',
            }}
          >
            {portalLoading ? 'Opening…' : 'Plan & Billing'}
          </button>
        ) : (
          <Link
            to="/upgrade"
            onClick={onMobileClose}
            style={{
              ...linkStyle(''),
              display: 'block',
              textDecoration: 'none',
            }}
          >
            Upgrade to Pro
          </Link>
        )}
        <button
          onClick={onSignOut}
          style={{
            ...linkStyle(''),
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            fontFamily: 'inherit',
          }}
        >
          Sign out
        </button>

        <div style={{ flex: 1 }} />

        {email && (
          <div
            style={{
              padding: '10px 12px',
              borderTop: '1px solid var(--nav-border)',
              fontSize: '12px',
              color: 'var(--text-muted)',
            }}
          >
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {email}
            </div>
            {usageRemaining !== null && (
              <div style={{ marginTop: '4px', color: 'var(--accent)' }}>
                {usageRemaining} interaction{usageRemaining === 1 ? '' : 's'} left
              </div>
            )}
          </div>
        )}
      </aside>

      <style>{`
        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
            transition: transform 0.2s ease;
          }
          .sidebar-open { transform: translateX(0); }
        }
        @media (min-width: 769px) {
          .sidebar-backdrop { display: none !important; }
        }
      `}</style>
    </>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '0 12px',
        marginBottom: '6px',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </div>
  );
}
